import { useEffect, useCallback } from 'react';
import { Select, Table, Card, Typography, Button } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useLLMOverrides } from './useLLMOverrides';
import { predefinedUseCases } from './config';
import { getLlmDisplayText, getLlmDisplayComponent } from 'src/utils/llmUtils';

const { Title, Text } = Typography;

const OrganizationLLMs = ({ selectedOrganization }) => {
  const {
    llms,
    llmsLoading,
    llmOverrides,
    llmOverridesLoading,
    initializeTabConfiguration,
    updateTabConfiguration,
    getTabConfiguration,
    getTabUnsavedChanges,
    saveTabConfigurations,
  } = useLLMOverrides();

  // Get configurations and unsaved changes from Redux
  const configurations = getTabConfiguration('org');
  const hasUnsavedChanges = getTabUnsavedChanges('org');

  // Initialize tab configuration when component mounts or when LLM overrides change
  useEffect(() => {
    initializeTabConfiguration('org', predefinedUseCases);
  }, [llmOverrides, llms?.llms, initializeTabConfiguration]);

  // Handle configuration updates
  const handleConfigurationChange = (
    useCaseKey,
    settingKey,
    optionKey,
    field,
    value
  ) => {
    updateTabConfiguration(
      'org',
      useCaseKey,
      settingKey,
      optionKey,
      field,
      value
    );
  };

  // Handle save configurations
  const handleSaveConfigurations = useCallback(async () => {
    await saveTabConfigurations('org', predefinedUseCases);
  }, [saveTabConfigurations]);

  // Get LLM options (excluding global model - users can clear to use global)
  const llmOptions =
    llms?.llms?.map((llm, index) => {
      const displayText = getLlmDisplayText(llm);

      return {
        key: llm.llm_id || `llm-${index}`, // Unique key for React
        label: displayText,
        value: llm.model,
        llm_id: llm.llm_id,
        llm: llm, // Store full llm object for rendering
      };
    }) || [];

  // Custom option render to show badges
  const renderOption = (option) => {
    if (!option.llm) return option.label;
    return getLlmDisplayComponent(option.llm);
  };

  // Create table columns for each use case
  const createTableColumns = () => [
    {
      title: 'Verbosity',
      dataIndex: 'value',
      key: 'value',
      width: '50%',
      render: (text) => <span className="text-gray-800 text-sm">{text}</span>,
    },
    {
      title: 'Model',
      dataIndex: 'llm',
      key: 'llm',
      width: '50%',
      render: (_, record) => (
        <Select
          value={
            configurations[record.useCaseKey]?.[record.settingKey]?.[
              record.optionKey
            ]?.llm || null
          }
          onChange={(value) =>
            handleConfigurationChange(
              record.useCaseKey,
              record.settingKey,
              record.optionKey,
              'llm',
              value
            )
          }
          options={llmOptions}
          loading={llmsLoading}
          className="w-full"
          size="small"
          popupMatchSelectWidth={false}
          showSearch
          allowClear
          placeholder="Select a model"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          optionRender={renderOption}
        />
      ),
    },
  ];

  // Create table data for a specific use case
  const createTableDataForUseCase = (useCaseKey) => {
    const useCase = predefinedUseCases[useCaseKey];
    const rows = [];

    Object.keys(useCase.settings).forEach((settingKey) => {
      const setting = useCase.settings[settingKey];

      setting.options.forEach((option) => {
        rows.push({
          key: `${useCaseKey}-${settingKey}-${option.toLowerCase()}`,
          useCaseKey,
          settingKey,
          optionKey: option.toLowerCase(),
          value: option,
        });
      });
    });

    return rows;
  };

  return (
    <div className="space-y-6">
      {Object.keys(predefinedUseCases).map((useCaseKey) => {
        const useCase = predefinedUseCases[useCaseKey];
        return (
          <Card key={useCaseKey}>
            <div className="mb-4">
              <Title level={4} className="!mb-1">
                {useCase.title}
              </Title>
              <Text type="secondary" className="text-sm">
                {useCase.description}
              </Text>
            </div>
            <Table
              dataSource={createTableDataForUseCase(useCaseKey)}
              columns={createTableColumns()}
              rowKey="key"
              pagination={false}
              size="middle"
              tableLayout="fixed"
              bordered
              loading={llmsLoading || llmOverridesLoading}
              locale={{ emptyText: 'No settings available.' }}
            />
          </Card>
        );
      })}

      {/* Save button positioned at bottom right */}
      <div className="flex justify-end pt-4">
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveConfigurations}
            loading={llmOverridesLoading}
            disabled={!hasUnsavedChanges || !selectedOrganization}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationLLMs;
